import React, { useState } from 'react';
import { connect } from 'react-redux';
import { List, Modal } from 'antd';
import {
  bulkActionTypeSelector,
  bulkActionStepSelector,
  isBulkActionWorkflowActiveSelector,
  sortedSelectedUserIdNameTuplesSelector,
  sortedSuccessUserIdNameTuplesSelector,
  sortedErrorUserIdNameTuplesSelector,
} from '../../../../redux/selectors/bulkActionsSelector';
import BULK_ACTIONS_MAP, {
  COMMENT_STEP,
  CONFIRM_STEP,
  SUBMITTING,
  FAILURE,
  SUCCESS,
} from '../../../../redux/constants/bulk_actions';
import {
  cancelBulkActionWorkflow,
  finishBulkActionWorkflow,
} from '../../../../redux/actions';
import BulkActionCommentForm from '../../ModalCommentForm/BulkActionCommentForm';
import ModalContext from '../ModalContext';
import BulkActionConfirmForm from '../../ModalConfirmForm/BulkActionConfirmForm';
import UserList from './UserList';
import Loader from '../../Loader';
import './index.css';

const noop = () => {};

const mapStateToProps = (state) => ({
  bulkActionType: bulkActionTypeSelector(state),
  bulkActionStep: bulkActionStepSelector(state),
  isActive: isBulkActionWorkflowActiveSelector(state),
  errorUsers: sortedErrorUserIdNameTuplesSelector(state),
  successUsers: sortedSuccessUserIdNameTuplesSelector(state),
  users: sortedSelectedUserIdNameTuplesSelector(state),
});

const mapDispatchToProps = {
  cancelWorkflow: cancelBulkActionWorkflow,
  finishWorkflow: finishBulkActionWorkflow,
};

function MainCopyBody({
  body,
  users,
  verificationNote,
}) {
  return (
    <>
      {body}
      <UserList users={users} />
      {
      verificationNote && (
        <>
          {verificationNote.body}
          <List
            dataSource={verificationNote.list}
            prefixCls="modal_verification_list ant-list"
            renderItem={(li) => (
              <li>{li}</li>
            )}
          />
        </>
      )
    }
    </>
  );
}
const getModalBody = ({
  bulkActionStep,
  copyConfig,
  users,
  successUsers,
  errorUsers,
}) => {
  switch (bulkActionStep) {
    case COMMENT_STEP:
      return (
        <>
          <MainCopyBody
            body={copyConfig.body}
            users={users}
            verificationNote={copyConfig.verificationNote}
          />
          <BulkActionCommentForm />
        </>
      );
    case CONFIRM_STEP:
      return (
        <>
          <MainCopyBody
            body={copyConfig.body}
            users={users}
            verificationNote={copyConfig.verificationNote}
          />
          <p>
            {`Type "${copyConfig.confirmationPhrase}" to confirm`}
          </p>
          <BulkActionConfirmForm
            confirmationPhrase={copyConfig.confirmationPhrase}
          />
        </>
      );
    case FAILURE:
      return (
        <>
          {copyConfig.errorBody}
        </>
      );
    case SUCCESS:
      return (
        <>
          { successUsers.length > 0 && (
            <>
              {copyConfig.successBody}
              <UserList users={successUsers} />
            </>
          ) }
          { errorUsers.length > 0 && (
            <>
              {copyConfig.partialErrorBody}
              <UserList users={errorUsers} />
            </>
          ) }
        </>
      );
    case SUBMITTING:
    default:
      return (
        <Loader isLoading />
      );
  }
};

const getOnCancel = ({
  bulkActionStep,
  cancelWorkflow,
  finishWorkflow,
}) => {
  switch (bulkActionStep) {
    case SUCCESS:
    case FAILURE:
      return finishWorkflow;
    default:
      return cancelWorkflow;
  }
};

const getOnOk = ({
  bulkActionStep,
  onCancel,
  submitFn,
}) => {
  switch (bulkActionStep) {
    case COMMENT_STEP:
    case CONFIRM_STEP:
      return submitFn;
    default:
      return onCancel;
  }
};

function BulkActionsWorkflowModal({
  bulkActionType,
  bulkActionStep,
  isActive,
  users,
  successUsers,
  errorUsers,
  cancelWorkflow,
  finishWorkflow,
}) {
  // used by nth-level children to set outer submit control
  const [submitFn, setSubmitFn] = useState(noop);
  // used by nth-level children to set outer submit control disabled state
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);

  const [modalContext] = useState({
    setSubmitFn,
    setSubmitDisabled,
  });

  const copyConfig = BULK_ACTIONS_MAP[bulkActionType];
  const isClosable = bulkActionStep !== SUBMITTING;
  const isCancelButtonHidden = [SUBMITTING, SUCCESS, FAILURE]
    .includes(bulkActionStep);
  const onCancel = getOnCancel({
    bulkActionStep,
    cancelWorkflow,
    finishWorkflow,
  });

  // disable the modal submit by the form's status if it's a form,
  // otherwise never disable it
  const isOkButtonDisabled = [COMMENT_STEP, CONFIRM_STEP]
    .includes(bulkActionStep) && isSubmitDisabled;
  const isOkButtonHidden = bulkActionStep === SUBMITTING;
  const onOk = getOnOk({
    bulkActionStep,
    onCancel,
    submitFn,
  });

  const footerOverride = {};
  // No buttons means no need for footer
  if (isOkButtonHidden && isCancelButtonHidden) {
    footerOverride.footer = null;
  }
  return (
    <Modal
      {...footerOverride}
      destroyOnClose
      closable={isClosable}
      keyboard={isClosable}
      maskClosable={isClosable}
      cancelButtonProps={{
        hidden: isCancelButtonHidden,
      }}
      okButtonProps={{
        disabled: isOkButtonDisabled,
        hidden: isOkButtonHidden,
      }}
      onCancel={onCancel}
      onOk={onOk}
      title={copyConfig.title}
      visible={isActive}
    >
      <ModalContext.Provider value={modalContext}>
        <div>
          {
            getModalBody({
              bulkActionStep,
              copyConfig,
              users,
              successUsers,
              errorUsers,
            })
          }
        </div>
      </ModalContext.Provider>
    </Modal>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(BulkActionsWorkflowModal);
